import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, TextField, IconButton, Paper, Divider,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableRow, TableContainer,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Swal from 'sweetalert2';
import { api } from '../../../api';
import MDDatePicker from 'components/MDDatePicker';
import RichTextEditor from 'components/RichTextEditor';
import ImportBloodHistoryDialog from './ImportBloodHistoryDialog';

const SECTIONS = [
  { key: 'hormonal',     label: 'Hormonal',      slug: 'blood-investigation-params-hormonal' },
  { key: 'serology',     label: 'Serology',       slug: 'blood-investigation-params-serology' },
  { key: 'biochemistry', label: 'Bio Chemistry',  slug: 'blood-investigation-params-bio-chemistry' },
];

function BloodTestsForm({ bloodParams, patientInfo, procedureId, existingHistories, canSave, canEdit, canDelete }) {
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';

  const [saving, setSaving] = useState({ hormonal: false, serology: false, biochemistry: false });
  const [histories, setHistories] = useState({ hormonal: [], serology: [], biochemistry: [] });
  const [editingHistory, setEditingHistory] = useState({}); // { [id]: { name, value, history_date, remarks, param_group_member_option_id } }
  const [updatingHistory, setUpdatingHistory] = useState(false);
  const [importDialog, setImportDialog] = useState({ open: false, section: null, slug: '', label: '' });

  const { control, watch, setValue, reset, getValues } = useForm({
    defaultValues: {
      hormonalForms: [],
      serologyForms: [],
      biochemistryForms: [],
    },
  });

  const { fields: hormonalFields, append: appendHormonal, remove: removeHormonal } =
    useFieldArray({ control, name: 'hormonalForms' });
  const { fields: serologyFields, append: appendSerology, remove: removeSerology } =
    useFieldArray({ control, name: 'serologyForms' });
  const { fields: biochemistryFields, append: appendBiochemistry, remove: removeBiochemistry } =
    useFieldArray({ control, name: 'biochemistryForms' });

  const getOptions = useCallback((slug) => {
    if (!bloodParams?.param_group_members) return [];
    const member = bloodParams.param_group_members.find(m => m.slug === slug);
    return member?.param_group_member_options || [];
  }, [bloodParams]);

  const hormonalOptions  = getOptions('blood-investigation-params-hormonal');
  const serologyOptions  = getOptions('blood-investigation-params-serology');
  const biochemistryOptions = getOptions('blood-investigation-params-bio-chemistry');

  // Organise existing histories by section when data arrives
  useEffect(() => {
    if (!existingHistories?.length || !bloodParams?.param_group_members) return;
    const organized = { hormonal: [], serology: [], biochemistry: [] };
    existingHistories.forEach(h => {
      const member = bloodParams.param_group_members.find(m => m.id === h.irm_param_group_members_id);
      if (!member) return;
      if (member.slug === 'blood-investigation-params-hormonal')      organized.hormonal.push(h);
      else if (member.slug === 'blood-investigation-params-serology')  organized.serology.push(h);
      else if (member.slug === 'blood-investigation-params-bio-chemistry') organized.biochemistry.push(h);
    });
    setHistories(organized);
  }, [existingHistories, bloodParams]);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      height: '40px',
      '&:hover fieldset': { borderColor: primaryColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor, borderWidth: 2 },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
    '& .MuiSelect-select': { paddingTop: '8px', paddingBottom: '8px' },
  };

  const addForm = useCallback((section) => {
    const fns = { hormonal: appendHormonal, serology: appendSerology, biochemistry: appendBiochemistry };
    fns[section]({ test_id: '', test_name: '', date: '', value: '', remarks: '' });
  }, [appendHormonal, appendSerology, appendBiochemistry]);

  const saveSection = useCallback(async (section, slug, label) => {
    const formKey = `${section}Forms`;
    const forms = getValues(formKey);
    const valid = forms.filter(f => f.test_id && f.date && f.value);

    if (valid.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Incomplete', text: 'Please fill in Test, Date and Value for at least one entry.', timer: 3000, showConfirmButton: false });
      return;
    }

    setSaving(prev => ({ ...prev, [section]: true }));
    try {
      const member = bloodParams?.param_group_members?.find(m => m.slug === slug);

      const procedurePayload = valid.map(f => ({
        irm_contacts_id: patientInfo.id,
        irm_param_groups_id: bloodParams?.id,
        irm_param_group_members_id: member?.id,
        param_group_member_option_id: parseInt(f.test_id),
        name: f.test_name,
        value: f.value,
        history_date: f.date,
        remarks: f.remarks || null,
        private_note: `initiation-${procedureId}`,
      }));

      const contactPayload = valid.map(f => ({
        contact_id: patientInfo.id,
        param_group_id: bloodParams?.id,
        param_group_member_id: member?.id,
        param_group_member_option_id: parseInt(f.test_id),
        name: f.test_name,
        value: f.value,
        history_date: f.date,
        remarks: f.remarks || null,
        private_note: `procedure-initiation-${procedureId}`,
      }));

      // Save procedure histories first and capture actual saved records
      const procedureResponse = await api.ivf.saveProcedureHistories(procedureId, procedurePayload);

      // NOTE: Contact history is NOT saved here for new records added via the form.
      // It is only saved when importing from old history (handled in handleImportHistory).

      // Use actual saved records from server response to update local list
      const savedRecords = procedureResponse?.data;
      if (Array.isArray(savedRecords) && savedRecords.length > 0) {
        setHistories(prev => ({
          ...prev,
          [section]: [...prev[section], ...savedRecords],
        }));
      } else {
        // Fallback optimistic update if server didn't return records
        setHistories(prev => ({
          ...prev,
          [section]: [
            ...prev[section],
            ...valid.map((f, i) => ({
              id: `new-${Date.now()}-${i}`,
              name: f.test_name,
              value: f.value,
              history_date: f.date,
              remarks: f.remarks || '',
            })),
          ],
        }));
      }

      // Clear saved section's form rows
      reset({ ...getValues(), [formKey]: [] });

      Swal.fire({ icon: 'success', title: 'Saved', text: `${label} data saved successfully.`, timer: 2500, timerProgressBar: true, showConfirmButton: false });
    } catch (err) {
      console.error('Error saving', section, err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to save. Please try again.' });
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  }, [bloodParams, patientInfo, procedureId, getValues, reset]);

  // ── Import handler ────────────────────────────────────────────────────────
  const handleImportHistory = useCallback(async (records) => {
    const { section, slug } = importDialog;
    const member = bloodParams?.param_group_members?.find(m => m.slug === slug);

    const procedurePayload = records.map(h => ({
      irm_contacts_id: patientInfo.id,
      irm_param_groups_id: bloodParams?.id,
      irm_param_group_members_id: member?.id,
      param_group_member_option_id: h.irm_param_group_member_options_id ?? h.param_group_member_option_id ?? null,
      name: h.param_group_member_option?.value || h.param_group_member_option?.option_value || h.name || '',
      value: h.value,
      history_date: h.history_date,
      remarks: h.remarks || null,
      private_note: `initiation-import-${procedureId}`,
    }));

    const res = await api.ivf.saveProcedureHistories(procedureId, procedurePayload);
    const savedRecords = Array.isArray(res?.data) ? res.data : [];

    setHistories(prev => ({
      ...prev,
      [section]: [
        ...prev[section],
        ...(savedRecords.length > 0 ? savedRecords : records.map((h, i) => ({
          id: `imported-${Date.now()}-${i}`,
          name: h.param_group_member_option?.value || h.name || '',
          value: h.value,
          history_date: h.history_date,
          remarks: h.remarks || '',
        }))),
      ],
    }));

    Swal.fire({ icon: 'success', title: 'Imported', text: `${records.length} record${records.length > 1 ? 's' : ''} imported successfully.`, timer: 2500, timerProgressBar: true, showConfirmButton: false });
  }, [importDialog, bloodParams, patientInfo, procedureId]);

  const openImportDialog = (section, slug, label) => setImportDialog({ open: true, section, slug, label });
  const closeImportDialog = () => setImportDialog({ open: false, section: null, slug: '', label: '' });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const handleEditHistory = (h) => {
    setEditingHistory(prev => ({
      ...prev,
      [h.id]: {
        name: h.name,
        value: h.value,
        history_date: h.history_date,
        remarks: h.remarks || '',
        param_group_member_option_id: h.irm_param_group_member_options_id || h.param_group_member_option_id || null,
      },
    }));
  };

  const handleCancelEdit = (id) => {
    setEditingHistory(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleSaveEdit = async (h, section) => {
    const edited = editingHistory[h.id];
    if (!edited) return;
    setUpdatingHistory(true);
    try {
      const payload = {
        name: edited.name,
        value: edited.value,
        history_date: edited.history_date,
        remarks: edited.remarks || null,
        irm_param_group_member_options_id: edited.param_group_member_option_id ? parseInt(edited.param_group_member_option_id) : null,
      };
      await api.ivf.updateProcedureHistory(h.id, payload);
      setHistories(prev => ({
        ...prev,
        [section]: prev[section].map(item =>
          item.id === h.id ? { ...item, ...edited } : item
        ),
      }));
      handleCancelEdit(h.id);
      Swal.fire({ icon: 'success', title: 'Updated', text: 'Record updated successfully.', timer: 2000, timerProgressBar: true, showConfirmButton: false });
    } catch (err) {
      console.error('Error updating history:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to update record.' });
    } finally {
      setUpdatingHistory(false);
    }
  };

  const handleDeleteHistory = async (h, section) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Record',
      text: `Delete "${h.name}" entry?`,
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      await api.ivf.deleteProcedureHistory(h.id);
      setHistories(prev => ({
        ...prev,
        [section]: prev[section].filter(item => item.id !== h.id),
      }));
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'Record deleted.', timer: 2000, timerProgressBar: true, showConfirmButton: false });
    } catch (err) {
      console.error('Error deleting history:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || 'Failed to delete record.' });
    }
  };

  const renderHistoryTable = (section, label, options) => {
    const data = histories[section];
    if (!data || data.length === 0) {
      return (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No existing {label.toLowerCase()} history found
          </Typography>
        </Paper>
      );
    }

    // Check if any row in this section is being edited
    const activeEditId = data.find(h => editingHistory[h.id]) ? data.find(h => editingHistory[h.id]).id : null;
    const edited = activeEditId ? editingHistory[activeEditId] : null;

    return (
      <Box mb={2}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Test Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px' }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '12px', textAlign: 'center', width: 80 }}>Actions</TableCell>
              </TableRow>
              {data.map(h => {
                const isEditing = !!editingHistory[h.id];
                return (
                  <TableRow key={h.id} hover sx={{ backgroundColor: isEditing ? `${primaryColor}08` : 'inherit' }}>
                    <TableCell sx={{ fontSize: '12px', fontWeight: isEditing ? 600 : 400, color: isEditing ? primaryColor : 'inherit' }}>
                      {h.name || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', maxWidth: 160 }}>
                      <Box dangerouslySetInnerHTML={{ __html: h.value || '-' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(h.history_date)}</TableCell>
                    <TableCell sx={{ fontSize: '12px', maxWidth: 160 }}>
                      <Box dangerouslySetInnerHTML={{ __html: h.remarks || '-' }} />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton size="small" onClick={() => handleSaveEdit(h, section)} disabled={updatingHistory}
                            sx={{ color: '#28a745', '&:hover': { backgroundColor: 'rgba(40,167,69,0.1)' } }} title="Save">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleCancelEdit(h.id)} disabled={updatingHistory}
                            sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(108,117,125,0.1)' } }} title="Cancel">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box display="flex" gap={0.5} justifyContent="center">
                          {canEdit !== false && !activeEditId && (
                            <IconButton size="small" onClick={() => handleEditHistory(h)}
                              sx={{ color: primaryColor, '&:hover': { backgroundColor: `${primaryColor}18` } }} title="Edit">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canDelete !== false && !activeEditId && (
                            <IconButton size="small" onClick={() => handleDeleteHistory(h, section)}
                              sx={{ color: '#dc3545', '&:hover': { backgroundColor: 'rgba(220,53,69,0.1)' } }} title="Delete">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Edit Form Card — shown below the table when a row is being edited */}
        {activeEditId && edited && (
          <Paper elevation={3} sx={{ mt: 1.5, p: 2.5, border: `2px solid ${primaryColor}`, borderRadius: 2, backgroundColor: '#f0fafb' }}>
            <Typography sx={{ fontWeight: 'bold', color: primaryColor, fontSize: '0.85rem', mb: 2 }}>
              ✏️ Editing: {data.find(h => h.id === activeEditId)?.name || ''}
            </Typography>

            {/* Row 1: Test Name dropdown + Date */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Test Name</InputLabel>
                  <Select
                    value={edited.param_group_member_option_id ? parseInt(edited.param_group_member_option_id) : ''}
                    label="Test Name"
                    onChange={(e) => {
                      const opt = options.find(o => o.id === e.target.value);
                      setEditingHistory(prev => ({
                        ...prev,
                        [activeEditId]: { ...prev[activeEditId], param_group_member_option_id: e.target.value, name: opt?.option_value || opt?.name || prev[activeEditId].name },
                      }));
                    }}
                  >
                    {options.map(opt => (
                      <MenuItem key={opt.id} value={opt.id}>{opt.option_value || opt.value || opt.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  label="Date"
                  value={edited.history_date || ''}
                  onChange={(date) => setEditingHistory(prev => ({ ...prev, [activeEditId]: { ...prev[activeEditId], history_date: date } }))}
                  textFieldProps={{ fullWidth: true, size: 'small', sx: inputSx }}
                />
              </Grid>
            </Grid>

            {/* Row 2: Value (left) + Remarks (right) */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <RichTextEditor
                  label="Value"
                  value={edited.value || ''}
                  onChange={(val) => setEditingHistory(prev => ({ ...prev, [activeEditId]: { ...prev[activeEditId], value: val } }))}
                  height="140px"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RichTextEditor
                  label="Remarks"
                  value={edited.remarks || ''}
                  onChange={(val) => setEditingHistory(prev => ({ ...prev, [activeEditId]: { ...prev[activeEditId], remarks: val } }))}
                  height="140px"
                />
              </Grid>
            </Grid>

            {/* Action buttons */}
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <button
                type="button"
                onClick={() => handleCancelEdit(activeEditId)}
                disabled={updatingHistory}
                style={{ padding: '8px 20px', background: 'transparent', color: '#6c757d', border: '1px solid #6c757d', borderRadius: 4, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}
              >
                Cancel
              </button>
              <Box
                component="button"
                type="button"
                onClick={() => handleSaveEdit(data.find(h => h.id === activeEditId), section)}
                disabled={updatingHistory}
                sx={{ padding: '8px 20px', background: updatingHistory ? '#ccc' : '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: updatingHistory ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <SaveIcon sx={{ fontSize: '1rem' }} />
                {updatingHistory ? 'Saving...' : 'Save Changes'}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  const renderSection = ({ key: section, slug, label }, fields, removeFunction, options) => {
    const isSaving = saving[section];
    const formKey = `${section}Forms`;
    const color = primaryColor;

    return (
      <Paper key={section} sx={{ mb: 3, boxShadow: '0 2px 4px rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden', border: `1px solid ${color}30` }}>
        {/* Section Header */}
        <Box sx={{
          px: 2, py: 1.5,
          borderBottom: `2px solid ${color}`,
          backgroundColor: `${color}0d`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Typography sx={{ color, fontWeight: 'bold', fontSize: '1rem' }}>{label}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <button
              type="button"
              onClick={() => openImportDialog(section, slug, label)}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                color: '#6c757d',
                border: '1px solid #ced4da',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <DownloadIcon sx={{ fontSize: '1rem' }} />
              Import History
            </button>
            <button
              type="button"
              onClick={() => addForm(section)}
              disabled={isSaving}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                color,
                border: `1px solid ${color}`,
                borderRadius: 4,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '0.8rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <AddIcon sx={{ fontSize: '1rem' }} />
              Add Test
            </button>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Existing History Table */}
          {renderHistoryTable(section, label, options)}

          <Divider sx={{ my: 2 }} />

          {/* Form Rows */}
          {fields.length === 0 ? (
            <Paper sx={{ p: 2.5, backgroundColor: '#f8f9fa', textAlign: 'center', borderRadius: 1, border: '1px dashed #dee2e6' }}>
              <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                No {label} tests added. Click &quot;Add Test&quot; above to get started.
              </Typography>
            </Paper>
          ) : (
            <>
              {fields.map((field, index) => (
                <Paper key={field.id} sx={{ p: 2, mb: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography sx={{ color, fontWeight: 'bold', fontSize: '0.85rem' }}>Test #{index + 1}</Typography>
                    <IconButton onClick={() => removeFunction(index)} size="small"
                      sx={{ color: '#dc3545', '&:hover': { backgroundColor: 'rgba(220,53,69,0.1)' } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`${formKey}.${index}.test_id`}
                        control={control}
                        render={({ field: f }) => (
                          <FormControl fullWidth size="small" sx={inputSx}>
                            <InputLabel>Test Name *</InputLabel>
                            <Select
                              {...f}
                              label="Test Name *"
                              onChange={(e) => {
                                f.onChange(e);
                                const opt = options.find(o => o.id === e.target.value);
                                if (opt) setValue(`${formKey}.${index}.test_name`, opt.option_value || opt.value || opt.name || '');
                              }}
                            >
                              {options.map(opt => (
                                <MenuItem key={opt.id} value={opt.id}>
                                  {opt.option_value || opt.value || opt.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`${formKey}.${index}.date`}
                        control={control}
                        render={({ field: f }) => (
                          <MDDatePicker
                            label="Date *"
                            value={f.value || ''}
                            onChange={async (date) => {
                              f.onChange(date);
                              if (date) {
                                const allRows = getValues(formKey);
                                const others = allRows
                                  .map((row, i) => ({ row, i }))
                                  .filter(({ row, i }) => i !== index && !row.date && (row.test_id || row.value));
                                if (others.length > 0) {
                                  const result = await Swal.fire({
                                    icon: 'question',
                                    title: 'Apply to all?',
                                    text: `Apply this date to ${others.length} other entr${others.length > 1 ? 'ies' : 'y'} without a date?`,
                                    showCancelButton: true,
                                    confirmButtonText: 'Yes, apply all',
                                    cancelButtonText: 'No',
                                    confirmButtonColor: primaryColor,
                                  });
                                  if (result.isConfirmed) {
                                    others.forEach(({ i }) => setValue(`${formKey}.${i}.date`, date));
                                  }
                                }
                              }
                            }}
                            textFieldProps={{
                              fullWidth: true,
                              size: 'small',
                              required: true,
                              sx: inputSx,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`${formKey}.${index}.value`}
                        control={control}
                        render={({ field: f }) => (
                          <RichTextEditor
                            label="Value *"
                            value={f.value || ''}
                            onChange={f.onChange}
                            height="120px"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`${formKey}.${index}.remarks`}
                        control={control}
                        render={({ field: f }) => (
                          <RichTextEditor
                            label="Remarks"
                            value={f.value || ''}
                            onChange={f.onChange}
                            height="120px"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}

              {/* Per-section Save button */}
              {canSave !== false && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <button
                    type="button"
                    onClick={() => saveSection(section, slug, label)}
                    disabled={isSaving}
                    style={{
                      padding: '10px 28px',
                      background: isSaving ? '#ccc' : primaryColor,
                      color: isSaving ? '#999' : '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'background 0.3s ease',
                    }}
                    onMouseOver={(e) => { if (!isSaving) e.currentTarget.style.background = '#005f70'; }}
                    onMouseOut={(e) => { if (!isSaving) e.currentTarget.style.background = primaryColor; }}
                  >
                    <SaveIcon fontSize="small" />
                    {isSaving ? 'Saving...' : `Save ${label}`}
                  </button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      {renderSection(SECTIONS[0], hormonalFields,     removeHormonal,     hormonalOptions)}
      {renderSection(SECTIONS[1], serologyFields,     removeSerology,     serologyOptions)}
      {renderSection(SECTIONS[2], biochemistryFields, removeBiochemistry, biochemistryOptions)}

      <ImportBloodHistoryDialog
        open={importDialog.open}
        onClose={closeImportDialog}
        contactId={patientInfo?.id}
        bloodParams={bloodParams}
        sectionSlug={importDialog.slug}
        sectionLabel={importDialog.label}
        onImport={handleImportHistory}
        primaryColor={primaryColor}
      />
    </Box>
  );
}

BloodTestsForm.propTypes = {
  bloodParams: PropTypes.shape({
    id: PropTypes.number,
    param_group_members: PropTypes.array,
  }),
  patientInfo: PropTypes.shape({
    id: PropTypes.number,
  }),
  procedureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  existingHistories: PropTypes.array,
  canSave: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
};

BloodTestsForm.defaultProps = {
  bloodParams: null,
  patientInfo: null,
  procedureId: null,
  existingHistories: [],
  canSave: true,
  canEdit: true,
  canDelete: true,
};

export default BloodTestsForm;

